import {AnimatePresence, motion} from 'framer-motion';
import {IconButton, Separator} from 'platform/components';
import {getCssSize, Heading, HStack, Scroll, Show, Space, VStack} from 'platform/foundation';
import {useTheme} from 'styled-components';

import {UserSidebarIconSize} from '@dms/api/shared';
import {usePermissions} from '@dms/shared';

import {suffixTestId, TestIdProps} from 'shared/web';

import {AccessTokenAndXBranchRow} from './components/AccessTokenAndXBranchRow';
import {AccountingDoorbellDebug} from './components/AccountingDoorbellDebug';
import {AiAssistantCrashTrigger} from './components/AiAssistantCrashTrigger';
import {AppUpdateSimulation} from './components/AppUpdateSimulation';
import {CacheCleanup} from './components/CacheCleanup';
import {ContentEditable} from './components/ContentEditable';
import {DevSettingsHeaderButton} from './components/DevSettingsHeaderButton';
import {FeatureFlagsMock} from './components/FeatureFlagsMock';
import {GridPresetReset} from './components/GridPresetReset';
import {LanguagesButtons} from './components/LanguagesButtons';
import {MandatoryFieldsValidationToggle} from './components/MandatoryFieldsValidationToggle';
import {PerformanceToggle} from './components/PerformancePanel/PerformanceToggle';
import {TimePickerShowcase} from './components/TimePickerShowcase';
import {TranslationDebug} from './components/TranslationDebug';
import {WarehouseArticleStressTest} from './components/WarehouseArticleStressTest';
import {WorktreeSelector} from './components/WorktreeSelector';
import {useDeveloperSettings} from './hooks/DeveloperSettingsProvider';
import {getFixedTranslation} from './utils/getFixedTranslation';

interface DevSettingsProps extends TestIdProps {
  iconSize?: UserSidebarIconSize;
}

export function DevSettingsSidebar(props: DevSettingsProps) {
  const theme = useTheme();

  const {open, isOpen, close, isAnyDevSettingEnabled} = useDeveloperSettings();

  const [canCreateExternalCheckoutDocuments] = usePermissions({
    permissionKeys: ['createExternalCheckoutDocuments'],
  });

  return (
    <>
      <DevSettingsHeaderButton
        isOpen={isOpen}
        onClick={open}
        isAnyDevSettingEnabled={isAnyDevSettingEnabled}
        iconSize={props.iconSize}
        data-testid={props['data-testid']}
      />

      <AnimatePresence mode="wait" initial={false}>
        {/* eslint-disable-next-line eag/prefer-show -- Pre-existing violation */}
        {isOpen && (
          <>
            <motion.div
              key="modal-background"
              initial={{opacity: 0}}
              animate={{opacity: 1}}
              exit={{opacity: 0}}
              transition={{duration: 0.3}}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: theme.zIndices.MODAL,
              }}
              onClick={close}
            />

            <motion.div
              key="modal-content"
              initial={{x: '100%'}}
              animate={{x: '0%'}}
              exit={{x: '100%'}}
              transition={{duration: 0.3}}
              style={{
                position: 'fixed',
                top: 0,
                right: 'var(--dms-assistant-gutter, 0)',
                maxWidth: '80vw',
                width: getCssSize(300),
                height: '100vh',
                backgroundColor: 'white',
                zIndex: theme.zIndices.MODAL_COMPONENT,
                boxShadow: '-5px 0px 15px rgba(0, 0, 0, 0.2)',
                padding: '20px',
              }}
            >
              <Scroll auto height="100vh">
                <HStack justify="space-between" align="center">
                  <Heading data-testid={suffixTestId('devSettings-activeBranch', props)} size={2}>
                    {getFixedTranslation('page.settings.labels.devSettings')}
                  </Heading>

                  <IconButton icon="content/clear" onClick={close} priority="secondary" />
                </HStack>

                <Separator />

                <HStack justify="space-between" align="center">
                  <LanguagesButtons />
                  <AccessTokenAndXBranchRow />
                </HStack>

                <Separator />

                <HStack spacing={2} wrap>
                  <TranslationDebug />
                  <ContentEditable />
                  <MandatoryFieldsValidationToggle />
                  <CacheCleanup />
                  <PerformanceToggle />
                </HStack>

                <Space vertical={4} />

                <VStack spacing={2}>
                  <FeatureFlagsMock />
                  {/* NOT WORKING, INTENTIONALLY COMMENTED OUT  */}
                  {/* <AclDebugCard /> */}
                  <WarehouseArticleStressTest />
                  <GridPresetReset />
                  <TimePickerShowcase />
                  <AiAssistantCrashTrigger />
                  <AppUpdateSimulation onSimulatePatch={close} />
                  {/* Nothing in the accounting doorbell works without external accounting: the
                      doorbell is not mounted on the checkout screen and the operation endpoint has
                      nothing to answer. Mounting the card anyway would only fire pointless
                      requests. */}
                  <Show when={canCreateExternalCheckoutDocuments}>
                    <AccountingDoorbellDebug />
                  </Show>

                  {/* Last: niche tool, only relevant when developing against a backend worktree. */}
                  <WorktreeSelector />
                </VStack>
              </Scroll>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
